import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Tooltip from "../../components/ui/Tooltip";

// Constants
const MAX_DROPDOWN_HEIGHT = 400;

const ReportingDropdown = ({
    handleDropdownSelectedReport,
    dropdownSelectedReport,
    reportDropdownOptions,
    viewtype,
    setApplyFilter,
}) => {
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);
    const optionRefs = useRef({});
    const [isOpen, setIsOpen] = useState(false);

    const groupedOptions = useMemo(() => {
        return reportDropdownOptions?.reduce((acc, option) => {
            if (!acc[option.ownerType]) {
                acc[option.ownerType] = [];
            }
            acc[option.ownerType].push(option);
            return acc;
        }, {});
    }, [reportDropdownOptions]);

    const flatOptions = useMemo(() => {
        return Object.values(groupedOptions || {}).flat();
    }, [groupedOptions]);

    const focusOptionAtIndex = useCallback(
        (index) => {
            if (!flatOptions.length) return;

            const boundedIndex =
                ((index % flatOptions.length) + flatOptions.length) %
                flatOptions.length;
            const option = flatOptions[boundedIndex];
            const optionElement = optionRefs.current[option.key];

            if (optionElement) {
                optionElement.focus();
                optionElement.scrollIntoView({ block: "nearest" });
            }
        },
        [flatOptions]
    );

    const getSelectedOrFirstIndex = useCallback(() => {
        const selectedIndex = flatOptions.findIndex(
            (option) => option.key === dropdownSelectedReport?.key
        );
        return selectedIndex >= 0 ? selectedIndex : 0;
    }, [dropdownSelectedReport, flatOptions]);

    const handleDropDownChange = useCallback((value) => {
        setIsOpen(false);
        handleDropdownSelectedReport(value);
        setApplyFilter(true);
    }, [handleDropdownSelectedReport, setApplyFilter]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const isClickOutsideDropdown =
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !toggleRef.current.contains(event.target);

            if (isClickOutsideDropdown) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const toggleDropdown = useCallback(() => {
        setIsOpen((prevState) => !prevState);
    }, []);

    const handleKeyDown = useCallback(
        (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsOpen((prevState) => !prevState);
                return;
            }

            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();

                if (!flatOptions.length) return;

                if (!isOpen) {
                    setIsOpen(true);
                    const startIndex =
                        e.key === "ArrowDown"
                            ? getSelectedOrFirstIndex()
                            : flatOptions.length - 1;
                    setTimeout(() => focusOptionAtIndex(startIndex), 0);
                    return;
                }

                const activeElement = document.activeElement;
                const currentIndex = flatOptions.findIndex(
                    (option) => optionRefs.current[option.key] === activeElement
                );

                const nextIndex =
                    e.key === "ArrowDown"
                        ? currentIndex + 1
                        : currentIndex - 1;

                focusOptionAtIndex(nextIndex);
                return;
            }

            if (e.key === "Escape") {
                e.preventDefault();
                setIsOpen(false);
            }
        },
        [
            flatOptions,
            isOpen,
            focusOptionAtIndex,
            getSelectedOrFirstIndex,
        ]
    );

    const handleOptionClick = useCallback(
        (option) => {
            if (dropdownSelectedReport?.key === option.key) {
                setIsOpen(false);
            } else {
                handleDropDownChange(option);
            }
        },
        [dropdownSelectedReport, handleDropDownChange]
    );

    const handleOptionKeyDown = useCallback(
        (e, option) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleDropDownChange(option);
                return;
            }

            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();

                const currentIndex = flatOptions.findIndex(
                    (flatOption) => flatOption.key === option.key
                );
                const nextIndex =
                    e.key === "ArrowDown"
                        ? currentIndex + 1
                        : currentIndex - 1;
                focusOptionAtIndex(nextIndex);
                return;
            }

            if (e.key === "Home") {
                e.preventDefault();
                focusOptionAtIndex(0);
                return;
            }

            if (e.key === "End") {
                e.preventDefault();
                focusOptionAtIndex(flatOptions.length - 1);
                return;
            }

            if (e.key === "Escape") {
                e.preventDefault();
                setIsOpen(false);
                toggleRef.current?.focus();
            }
        },
        [handleDropDownChange, flatOptions, focusOptionAtIndex]
    );

    const getOptionStyle = useCallback(
        (optionKey) => ({
            backgroundColor:
                dropdownSelectedReport?.key === optionKey
                    ? "#037cb7"
                    : "transparent",
            color:
                dropdownSelectedReport?.key === optionKey ? "white" : "#525252",
            width: "100%",
            gap: 4,
        }),
        [dropdownSelectedReport]
    );

    return (
        <div style={{ position: "relative" }}>
            {/* Toggle */}
            <div
                ref={toggleRef}
                className="d-flex justify-content-between cursor-pointer customFilterSection odd br-4p px-8p"
                onClick={toggleDropdown}
                onKeyDown={handleKeyDown}
                role="combobox"
                aria-expanded={isOpen}
                aria-controls="multi-select-dropdown"
                aria-label={viewtype}
                tabIndex={0}
                id={`${viewtype}`}
            >
                <div
                    className="customFilterDropDown"
                    style={{
                        paddingBottom: 0,
                        color: "#2E2E2E",
                        fontSize: 12.8,
                    }}
                >
                    {dropdownSelectedReport?.label || "Select a report"}
                </div>
                <i
                    className={`fa-solid fa-angle-${isOpen ? "up" : "down"}`}
                    style={{
                        fontSize: 16,
                        color: "#2E2E2E",
                        alignContent: "center",
                    }}
                    aria-hidden="true"
                />
            </div>

            {isOpen && (
                <div
                    className="custom-dropdown-container mt-5p"
                    style={{ paddingRight: 0 }}
                >
                    <div
                        ref={dropdownRef}
                        className="section-card custom-section-dropdown py-8p br-4p"
                    >
                        <div
                            className="custom-scrollbar"
                            style={{
                                maxHeight: MAX_DROPDOWN_HEIGHT,
                                borderTop: 0,
                            }}
                        >
                            {Object.entries(groupedOptions).map(
                                ([group, options]) => (
                                    <div
                                        key={group}
                                        role="group"
                                        aria-labelledby={`group-label-${group}`}
                                    >
                                        <div
                                            className="fw-bold py-8p px-16p fs"
                                            id={`group-label-${group}`}
                                        >
                                            {group}
                                        </div>

                                        {options.map((option) => (
                                            <div
                                                key={option.key}
                                                ref={(element) => {
                                                    if (element) {
                                                        optionRefs.current[
                                                            option.key
                                                        ] = element;
                                                    }
                                                }}
                                                className="multi-select-option py-8p px-16p fw-normal"
                                                style={getOptionStyle(
                                                    option.key
                                                )}
                                                onClick={() =>
                                                    handleOptionClick(option)
                                                }
                                                onKeyDown={(e) =>
                                                    handleOptionKeyDown(
                                                        e,
                                                        option
                                                    )
                                                }
                                                tabIndex={0}
                                                aria-label={option.label}
                                                role="option"
                                                aria-selected={
                                                    dropdownSelectedReport?.key ===
                                                    option.key
                                                }
                                                id={`option-${option.key}`}
                                            >
                                                <label
                                                    className="cursor-pointer"
                                                    style={{
                                                        fontWeight: 400,
                                                        padding: 0,
                                                        textAlign: "left",
                                                    }}
                                                >
                                                    {option.label}
                                                </label>
                                                <Tooltip
                                                    content={option.description}
                                                    tooltipId={`report-tooltip-${option.key}`}
                                                    tooltipClassName="reporting-dropdown-tooltip"
                                                    arrowClassName="reportingTooltipArrow"
                                                    portal
                                                    icon
                                                    iconAriaLabel={`Information about ${option.label}`}
                                                    iconColor={
                                                        dropdownSelectedReport?.key === option.key
                                                            ? "white"
                                                            : "#015294"
                                                    }
                                                    iconButtonStyle={{
                                                        background: "transparent",
                                                        border: 0,
                                                        padding: "0 0 0 4px",
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

ReportingDropdown.propTypes = {
    handleDropdownSelectedReport: PropTypes.func.isRequired,
    dropdownSelectedReport: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        ownerType: PropTypes.string,
    }),
    reportDropdownOptions: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            label: PropTypes.string.isRequired,
            ownerType: PropTypes.string.isRequired,
        })
    ).isRequired,
    setApplyFilter: PropTypes.func,
    viewtype: PropTypes.string,
};

export default ReportingDropdown;
